import { useEffect, useRef, useState, type SVGProps } from "react";

const X_JITTER = [0, -7, 5, -3, 8, -5, 3, -8, 6, -2] as const;
const Y_JITTER = [0, 3, -1, 2, -2, 1, 3, -2, 1, -1] as const;

/**
 * Counts 1 -> 100 in step with the herd rows filling in below it. The count
 * accelerates (ease-in) because that is the argument of the section: growth
 * compounds. Pre-rendered HTML and reduced-motion users get the final
 * "100 / 100"; the count only plays once, when the figure scrolls into view.
 */
export function HerdCount({ className }: { className?: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const [count, setCount] = useState(100);
  // Same SSR-flash gate as the hero's StatCount: hidden until the count plays,
  // "armed" cancels the CSS failsafe once JS is confirmed alive.
  const [phase, setPhase] = useState<"idle" | "armed" | "live">("idle");

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      setPhase("live"); // no counting: show the final value immediately
      return;
    }
    setPhase("armed");

    // Time-based interval rather than rAF: rAF pauses entirely in hidden or
    // zero-size renderers, which would freeze the count at 1 forever.
    let timer: ReturnType<typeof setInterval> | undefined;
    const play = () => {
      const t0 = performance.now();
      const DURATION = 2100; // rows land at ~2.2s; the counter arrives just before the 100th horse pops
      setCount(1);
      setPhase("live");
      timer = setInterval(() => {
        const p = Math.min(1, (performance.now() - t0) / DURATION);
        setCount(Math.max(1, Math.round(100 * Math.pow(p, 1.35))));
        if (p >= 1) clearInterval(timer);
      }, 32);
    };

    // Fail open (matches Reveal): no IO or a zero-height viewport means
    // intersection will never fire, so play on mount instead.
    if (typeof IntersectionObserver === "undefined" || window.innerHeight === 0) {
      play();
      return () => clearInterval(timer);
    }

    // Same trigger geometry as Reveal, so the count starts with the row fades.
    const io = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        io.disconnect();
        play();
      },
      { threshold: 0.12, rootMargin: "0px 0px -8% 0px" },
    );
    io.observe(el);
    return () => {
      io.disconnect();
      clearInterval(timer);
    };
  }, []);

  return (
    <span
      ref={ref}
      className={`count-fx ${phase === "armed" ? "count-armed" : ""} ${phase === "live" ? "count-live" : ""} ${className ?? ""}`}
    >
      <span className="tabular-nums">{count}</span> / 100
    </span>
  );
}

/** Exactly 100 horses, arranged with a little perspective rather than a grid. */
export function GrowthHerd(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 960 430" fill="none" aria-hidden="true" {...props}>
      <defs>
        <g id="growth-horse-shape">
          <path d="M31 28C22 27 14 21 8 11C14 13 20 17 26 23L33 26Z" fill="currentColor" />
          <g fill="none" stroke="currentColor" strokeWidth="5" strokeLinecap="round">
            <path d="M34 39L27 59" />
            <path d="M42 40L41 61" />
            <path d="M57 40L61 60" />
            <path d="M63 38L73 57" />
          </g>
          <ellipse cx="48" cy="33" rx="22" ry="10.5" fill="currentColor" />
          <path
            d="M54 31C59 19 63 11 71 6C76 2 81 3 85 6L94 12C97 14 96 18 93 19C90 20 87 18 84 17C80 17 77 20 74 24C70 29 66 33 61 36Z"
            fill="currentColor"
          />
          <path d="M73 7L76 -1L82 6Z" fill="currentColor" />
          <circle cx="82" cy="10" r="1.4" fill="var(--leaf)" />
        </g>

        <linearGradient id="growth-herd-haze" x1="480" y1="0" x2="480" y2="430" gradientUnits="userSpaceOnUse">
          <stop stopColor="var(--paper)" stopOpacity="0.68" />
          <stop offset="0.42" stopColor="var(--paper)" stopOpacity="0" />
        </linearGradient>
      </defs>

      <g className="growth-herd-contours" fill="none" stroke="currentColor" strokeWidth="1">
        <path d="M18 104C157 80 279 98 401 82C551 62 703 74 941 44" />
        <path d="M8 191C176 160 310 182 459 156C628 126 757 149 950 116" />
        <path d="M4 293C183 257 334 281 495 254C659 227 798 242 956 214" />
        <path d="M0 400C189 360 347 383 525 354C681 329 818 339 960 313" />
      </g>

      {Array.from({ length: 10 }, (_, row) => {
        const scale = 0.27 + row * 0.035;
        const y = 29 + row * 38;
        const xStart = 42 + (row % 2) * 13;

        return (
          <g key={row} className={`growth-herd-row growth-herd-row-${row + 1}`}>
            {Array.from({ length: 10 }, (_, column) => {
              const x = xStart + column * 86 + X_JITTER[(column + row) % X_JITTER.length]!;
              const horseY = y + Y_JITTER[(column * 3 + row) % Y_JITTER.length]!;
              const isBreakpoint = row === 9 && column === 9;

              // The 100th horse pops in last (CSS animates the inner <use>;
              // the outer <g> keeps the layout transform, so they don't fight).
              if (isBreakpoint) {
                return (
                  <g key={column} transform={`translate(${x} ${horseY}) scale(${scale})`}>
                    <use href="#growth-horse-shape" className="growth-herd-breakpoint" />
                  </g>
                );
              }

              return (
                <use
                  key={column}
                  href="#growth-horse-shape"
                  transform={`translate(${x} ${horseY}) scale(${scale})`}
                />
              );
            })}
          </g>
        );
      })}

      <rect width="960" height="108" fill="url(#growth-herd-haze)" />
    </svg>
  );
}
